// src/accounts/pages/AdminHome.js
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";

export default function AdminHome() {
  const [loading, setLoading] = useState(true);

  const [usersCount, setUsersCount] = useState(0);
  const [activeOrdersCount, setActiveOrdersCount] = useState(0);
  const [subscribersCount, setSubscribersCount] = useState(0);

  // ✅ NEW
  const [totalSearches, setTotalSearches] = useState(0);

  const [recentOrders, setRecentOrders] = useState([]);

  // ✅ NEW: site-wide activity (birthday searches)
  const [activityLoading, setActivityLoading] = useState(true);
  const [activity, setActivity] = useState([]);
  const [activityError, setActivityError] = useState("");

  useEffect(() => {
    let alive = true;

    async function load() {
      setLoading(true);

      // Try a single stats endpoint first (if you have it)
      try {
        const { data } = await api.get("admin/stats");
        if (!alive) return;

        if (data?.ok) {
          setUsersCount(Number(data?.usersCount || 0));
          setActiveOrdersCount(Number(data?.activeOrdersCount || 0));
          setSubscribersCount(Number(data?.subscribersCount || 0));
          setRecentOrders(Array.isArray(data?.recentOrders) ? data.recentOrders : []);

          // ✅ If you add this into admin/stats, it will auto show
          setTotalSearches(Number(data?.totalSearches || data?.searchesCount || 0));

          setLoading(false);
          return;
        }
      } catch {
        // ignore, use fallback below
      }

      // Fallback: separate endpoints (safe if some are missing)
      const safeGet = async (url, pick) => {
        try {
          const { data } = await api.get(url);
          return pick?.(data) ?? data;
        } catch {
          return null;
        }
      };

      const [uCount, oCount, sCount, orders, searchesCount] = await Promise.all([
        safeGet("/api/admin/users/count", (d) => (d?.ok ? d?.count : null)),
        safeGet("/api/admin/orders/count?status=active", (d) => (d?.ok ? d?.count : null)),
        safeGet("/api/admin/subscribers/count", (d) => (d?.ok ? d?.count : null)),
        safeGet("/api/admin/orders?limit=10", (d) => (d?.ok ? d?.rows || d?.orders : null)),

        // ✅ NEW: if you add this endpoint it will show total searches
        // GET /api/admin/search-history/count  -> { ok:true, count: 123 }
        safeGet("/api/admin/search-history/count", (d) => (d?.ok ? d?.count : null)),
      ]);

      if (!alive) return;

      setUsersCount(Number(uCount || 0));
      setActiveOrdersCount(Number(oCount || 0));
      setSubscribersCount(Number(sCount || 0));
      setRecentOrders(Array.isArray(orders) ? orders : []);
      setTotalSearches(Number(searchesCount || 0));

      setLoading(false);
    }

    load();
    return () => {
      alive = false;
    };
  }, []);

  // ✅ NEW: load site-wide search history from DB
  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setActivityError("");
        setActivityLoading(true);

        // ✅ IMPORTANT: this must come from DB
        // Endpoint should query user_search_history and return rows
        const r = await api.get("admin/search-history?limit=30&order=desc");

        if (!alive) return;

        const items = Array.isArray(r?.data?.items) ? r.data.items : [];
        setActivity(items);

        // If backend doesn't provide totalSearches, we can still show
        // at least the count of loaded items (not perfect but better than 0)
        if (!totalSearches && items.length) {
          // don't overwrite if you already have a real count
          // (this is just a UI-friendly fallback)
          setTotalSearches((prev) => (prev ? prev : items.length));
        }
      } catch (e) {
        if (!alive) return;
        setActivity([]);
        setActivityError(
          e?.response?.data?.error ||
            e?.response?.data?.message ||
            e?.message ||
            "Failed to load search history"
        );
      } finally {
        if (alive) setActivityLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- Helpers ----------
  const formatPrettyDate = (iso) => {
    if (!iso) return "";
    const v = String(iso).slice(0, 10);
    const [y, m, d] = v.split("-");
    if (!y || !m || !d) return v;
    const dt = new Date(Number(y), Number(m) - 1, Number(d));
    const month = dt.toLocaleString("en-US", { month: "long" });
    return `${String(d).padStart(2, "0")} - ${month} - ${y}`;
  };

  const formatTime = (ts) => {
    if (!ts) return "";
    const dt = new Date(ts);
    if (Number.isNaN(dt.getTime())) return "";
    return dt.toLocaleString("en-AU", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getActorLabel = (it) => {
    // expected join fields: user_email, user_name, user_first_name, user_last_name
    const name =
      it?.user_name ||
      [it?.user_first_name, it?.user_last_name].filter(Boolean).join(" ") ||
      "";
    const email = it?.user_email || "";

    if (name && email) return `${name} (${email})`;
    if (email) return email;
    if (name) return name;

    if (it?.user_id) return `User #${it.user_id}`;
    return "Guest";
  };

  const getResultUrl = (it) => {
    // stored URL like "/five-elements-zodiac/fire-horse"
    if (it?.result_url) return it.result_url;

    // fallback: go to search page by DOB
    const dob = it?.birthdate ? String(it.birthdate).slice(0, 10) : "";
    if (dob) return `/search?dob=${dob}`;
    return "";
  };

  const latestActivity = useMemo(() => {
    return Array.isArray(activity) && activity.length ? activity[0] : null;
  }, [activity]);

  const previousActivity = useMemo(() => {
    if (!Array.isArray(activity) || activity.length <= 1) return [];
    const latestId = activity[0]?.id;
    return latestId ? activity.slice(1).filter((x) => x?.id !== latestId) : activity.slice(1);
  }, [activity]);

  const renderActivityRow = (item) => {
    const dob = item?.birthdate ? String(item.birthdate).slice(0, 10) : "";
    const url = getResultUrl(item);
    const dateLabel = dob ? formatPrettyDate(dob) : item?.input_text || "View";
    const timeLabel = item?.created_at ? formatTime(item.created_at) : "";
    const resultLabel = item?.result_text || "";
    const actor = getActorLabel(item);

    return (
      <div key={item?.id || `${dob}-${url}-${actor}`} style={{ padding: "10px 0" }}>
        <div style={{ fontWeight: 800 }}>
          {url ? (
            <Link
              to={url}
              style={{
                fontWeight: 900,
                textDecoration: "underline",
                color: "inherit",
              }}
            >
              {dateLabel}
            </Link>
          ) : (
            dateLabel
          )}
        </div>

        <div style={{ marginTop: 4, opacity: 0.75, fontSize: 13 }}>
          <b>{actor}</b>
          {timeLabel ? <span> · {timeLabel}</span> : null}
        </div>

        {resultLabel ? (
          <div style={{ marginTop: 4, opacity: 0.85, fontSize: 13 }}>
            Result: <b>{resultLabel}</b>
          </div>
        ) : null}

        <div style={{ height: 1, background: "#e6e6e6", marginTop: 10 }} />
      </div>
    );
  };

  return (
    <>
      {/* Top cards */}
      <div style={cards}>
        <Card title="Users" value={loading ? "…" : usersCount} />
        <Card title="Active orders" value={loading ? "…" : activeOrdersCount} />
        <Card title="Subscribers" value={loading ? "…" : subscribersCount} />

        {/* ✅ NEW card */}
        <Card title="Total searches" value={loading ? "…" : totalSearches} />
      </div>

      {/* Recent orders */}
      <section style={panel}>
        <h2>Recent orders</h2>

        {loading ? (
          <p>Loading...</p>
        ) : recentOrders?.length ? (
          <div style={{ overflowX: "auto" }}>
            <table style={table}>
              <thead>
                <tr>
                  <th style={th}>Order</th>
                  <th style={th}>Customer</th>
                  <th style={th}>Total</th>
                  <th style={th}>Status</th>
                  <th style={th}>Created</th>
                </tr>
              </thead>
              <tbody>
                {recentOrders.map((o, idx) => (
                  <tr key={o?.id || o?.order_id || idx}>
                    <td style={td}>{o?.id ?? o?.order_id ?? "-"}</td>
                    <td style={td}>{o?.email ?? o?.customer_email ?? o?.user_email ?? "-"}</td>
                    <td style={td}>
                      {o?.total != null ? String(o.total) : o?.amount != null ? String(o.amount) : "-"}
                    </td>
                    <td style={td}>{o?.status ?? "-"}</td>
                    <td style={td}>{(o?.created_at || o?.createdAt || "").toString() || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p>No orders yet.</p>
        )}
      </section>

      {/* Site-wide searches */}
      <section style={panel}>
        <h2>Recent birthday searches (site-wide)</h2>

        {activityLoading ? (
          <p>Loading...</p>
        ) : activityError ? (
          <p style={{ color: "#8a0000", fontWeight: 700 }}>
            {activityError}
          </p>
        ) : !latestActivity ? (
          <p>No searches yet.</p>
        ) : (
          <>
            {/* Latest */}
            <div style={{ marginTop: 8 }}>
              <div style={{ height: 1, background: "#e6e6e6", margin: "10px 0" }} />

              <div style={{ fontSize: 13, opacity: 0.95 }}>
                <div style={{ fontWeight: 800 }}>
                  <span style={{ opacity: 0.85 }}>Birthday searched:</span>{" "}
                  <Link
                    to={getResultUrl(latestActivity) || "/"}
                    style={{
                      fontWeight: 900,
                      textDecoration: "underline",
                      color: "inherit",
                    }}
                  >
                    {latestActivity?.birthdate
                      ? formatPrettyDate(String(latestActivity.birthdate).slice(0, 10))
                      : latestActivity?.input_text || "View"}
                  </Link>
                </div>

                <div style={{ marginTop: 6, opacity: 0.75 }}>
                  <b>{getActorLabel(latestActivity)}</b>
                  {latestActivity?.created_at ? (
                    <span> · {formatTime(latestActivity.created_at)}</span>
                  ) : null}
                </div>

                {latestActivity?.result_text ? (
                  <div style={{ marginTop: 6, opacity: 0.85 }}>
                    Result: <b>{latestActivity.result_text}</b>
                  </div>
                ) : null}
              </div>

              <div style={{ height: 1, background: "#e6e6e6", margin: "10px 0 0" }} />
            </div>

            {/* More */}
            <div style={{ marginTop: 12 }}>
              <h3 style={{ margin: "10px 0 6px" }}>More recent searches</h3>

              {previousActivity.length ? (
                <div style={{ opacity: 1 }}>
                  <div style={{ height: 1, background: "#e6e6e6" }} />
                  {previousActivity.slice(0, 20).map(renderActivityRow)}
                </div>
              ) : (
                <p>No more results yet.</p>
              )}
            </div>
          </>
        )}
      </section>
    </>
  );
}

function Card({ title, value }) {
  return (
    <div style={card}>
      <div style={{ fontSize: 12, fontWeight: 900 }}>{title}</div>
      <div style={{ fontSize: 28, fontWeight: 900 }}>{value}</div>
    </div>
  );
}

const cards = {
  display: "grid",
  // ✅ now 4 cards
  gridTemplateColumns: "repeat(4, 1fr)",
  gap: 12,
};

const card = {
  padding: 14,
  borderRadius: 16,
  border: "1px solid rgba(0,0,0,0.1)",
};

const panel = {
  marginTop: 16,
  padding: 14,
  borderRadius: 16,
  border: "1px solid rgba(0,0,0,0.1)",
};

const table = {
  width: "100%",
  borderCollapse: "separate",
  borderSpacing: 0,
};

const th = {
  textAlign: "left",
  fontSize: 12,
  padding: "10px 8px",
  borderBottom: "1px solid rgba(0,0,0,0.08)",
  whiteSpace: "nowrap",
};

const td = {
  padding: "10px 8px",
  borderBottom: "1px solid rgba(0,0,0,0.06)",
  fontSize: 13,
  verticalAlign: "top",
  whiteSpace: "nowrap",
};